import { Fragment, useEffect, useRef, useState } from "react";
import type { ImportErgebnis, Kunde, KundeEingabe } from "../../shared/types.js";
import { api, nachladeFehler } from "../api.js";
import { leseKundendatei, vorlageCsv, type DateiErgebnis } from "../kundenDatei.js";

/**
 * Blockgröße für den Import. Der Browser zerlegt größere Dateien und zeigt
 * den Fortschritt — so bleibt jede einzelne Anfrage klein genug für das
 * CPU-Budget des Free-Plans.
 */
const BLOCK = 100;

const leer: KundeEingabe = {
  firma: "",
  strasse: "",
  plz: "",
  ort: "",
  ansprechpartner: "",
  email: "",
  telefon: "",
  zahlungsziel_tage: 14,
};

export function Kunden() {
  const [liste, setListe] = useState<Kunde[] | null>(null);
  const [offen, setOffen] = useState<string | null>(null);
  /** Mahlerzeugnisse für den Sortiment-Editor — nur die haben einen Mehlpreis. */
  const [artikel, setArtikel] = useState<{ id: string; sku: string; bezeichnung: string }[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [erfolg, setErfolg] = useState<string | null>(null);
  const [suche, setSuche] = useState("");

  function laden() {
    api
      .get<Kunde[]>("/kunden")
      .then(setListe)
      .catch((e) => setFehler(e.message));
  }
  useEffect(laden, []);

  /*
   * Die Artikelliste einmal beim Aufbau, nicht je aufgeklapptem Kunden.
   * `/preisfindung` liefert genau die Mahlerzeugnisse, um die es geht.
   */
  useEffect(() => {
    api
      .get<{ artikel: { id: string; sku: string; bezeichnung: string }[] }>("/preisfindung")
      .then((d) => setArtikel(d.artikel))
      .catch(() => setArtikel([]));
  }, []);

  const gefiltert = (liste ?? []).filter((k) =>
    [k.firma, k.kundennr, k.ort, k.plz]
      .filter(Boolean)
      .some((f) => String(f).toLowerCase().includes(suche.toLowerCase())),
  );

  return (
    <>
      {fehler && (
        <div className="meldung fehler" role="alert">
          {fehler}
        </div>
      )}
      {erfolg && (
        <div className="meldung erfolg" role="status">
          {erfolg}
        </div>
      )}

      <EinzelnAnlegen
        beiErfolg={(m) => {
          setErfolg(m);
          setFehler(null);
          laden();
        }}
        beiFehler={(m) => {
          setFehler(m);
          setErfolg(null);
        }}
      />

      <Massenimport
        beiFertig={(m) => {
          setErfolg(m);
          setFehler(null);
          laden();
        }}
      />

      <section className="karte">
        <div className="pos-kopf">
          <h2 style={{ margin: 0 }}>
            Kundenstamm{liste && <span style={{ color: "var(--faded)" }}> · {liste.length}</span>}
          </h2>
          <input
            style={{ maxWidth: 260 }}
            placeholder="Suchen …"
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
          />
        </div>

        {!liste && <p className="leer">Wird geladen …</p>}
        {liste && !liste.length && (
          <p className="leer">Noch keine Kunden. Oben einzeln anlegen oder Liste hochladen.</p>
        )}

        {liste && liste.length > 0 && (
          <div className="tabelle-scroll">
            <table>
              <thead>
                <tr>
                  <th>Nummer</th>
                  <th>Firma</th>
                  <th>Anschrift</th>
                  <th>Ansprechpartner</th>
                  <th>Kontakt</th>
                  <th className="zahl">Entfernung</th>
                  <th className="zahl">Zahlungsziel</th>
                  <th>Sortiment</th>
                </tr>
              </thead>
              <tbody>
                {gefiltert.map((k) => (
                  <Fragment key={k.id}>
                  <tr>
                    <td style={{ whiteSpace: "nowrap" }}>{k.kundennr}</td>
                    <td style={{ fontWeight: 600 }}>{k.firma}</td>
                    <td>
                      {k.strasse}
                      {k.strasse && (k.plz || k.ort) && <br />}
                      {[k.plz, k.ort].filter(Boolean).join(" ")}
                    </td>
                    <td>{k.ansprechpartner ?? "—"}</td>
                    <td style={{ fontSize: "0.85em" }}>{k.email ?? "—"}</td>
                    <td className="zahl">
                      <Entfernung kunde={k} beiFehler={setFehler} />
                    </td>
                    <td className="zahl">{k.zahlungsziel_tage} Tage</td>
                    <td>
                      <button
                        type="button"
                        className="knopf schlicht"
                        aria-expanded={offen === k.id}
                        onClick={() => setOffen(offen === k.id ? null : k.id)}
                      >
                        {k.produkt_ids?.length
                          ? `${k.produkt_ids.length} Artikel`
                          : "alle Artikel"}
                      </button>
                    </td>
                  </tr>
                  {offen === k.id && (
                    <tr>
                      <td colSpan={8}>
                        <Sortiment
                          kunde={k}
                          artikel={artikel}
                          beiFehler={setFehler}
                          beiGespeichert={(ids) => {
                            setListe((l) =>
                              l?.map((x) => (x.id === k.id ? { ...x, produkt_ids: ids } : x)) ?? l,
                            );
                            setOffen(null);
                          }}
                        />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
            {!gefiltert.length && <p className="leer">Kein Treffer für „{suche}“.</p>}
          </div>
        )}
      </section>
    </>
  );
}

/* ── Entfernung ──────────────────────────────────────────────────────────── */

/**
 * Direkt in der Liste pflegbar.
 *
 * Die Entfernung geht ueber die Frachtformel in jeden Preis ein und fehlt
 * bei jedem Bestandskunden, der vor der Preisfindung angelegt wurde. Eine
 * eigene Bearbeitungsmaske dafuer zu oeffnen waere ein Umweg fuer eine
 * dreistellige Zahl.
 */
function Entfernung({
  kunde,
  beiFehler,
}: {
  kunde: Kunde;
  beiFehler: (m: string) => void;
}) {
  const [wert, setWert] = useState(kunde.entfernung_km?.toString() ?? "");
  const [gespeichert, setGespeichert] = useState(kunde.entfernung_km?.toString() ?? "");
  const [laeuft, setLaeuft] = useState(false);

  async function sichern() {
    const roh = wert.trim();
    if (roh === gespeichert) return;
    setLaeuft(true);
    try {
      await api.patch(`/kunden/${kunde.id}`, { entfernung_km: roh === "" ? null : Number(roh) });
      setGespeichert(roh);
    } catch (e) {
      setWert(gespeichert); // zurueck auf den Stand der Datenbank
      beiFehler(e instanceof Error ? e.message : "Speichern fehlgeschlagen.");
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <input
      className="zahl"
      inputMode="numeric"
      style={{ width: "5.5rem", padding: "0.2rem 0.4rem", textAlign: "right" }}
      placeholder="— km"
      disabled={laeuft}
      value={wert}
      onChange={(e) => setWert(e.target.value.replace(/[^\d]/g, ""))}
      onBlur={sichern}
      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
      aria-label={`Entfernung ${kunde.firma}`}
    />
  );
}

/* ── Einzelne Firma ──────────────────────────────────────────────────────── */

function EinzelnAnlegen({
  beiErfolg,
  beiFehler,
}: {
  beiErfolg: (m: string) => void;
  beiFehler: (m: string) => void;
}) {
  const [offen, setOffen] = useState(false);
  const [k, setK] = useState<KundeEingabe>(leer);
  const [laeuft, setLaeuft] = useState(false);

  const setzen = (teil: Partial<KundeEingabe>) => setK((a) => ({ ...a, ...teil }));

  async function absenden(e: React.FormEvent) {
    e.preventDefault();
    setLaeuft(true);
    try {
      const a = await api.post<{ kundennr: string; firma: string }>("/kunden", k);
      beiErfolg(`${a.firma} wurde als ${a.kundennr} angelegt.`);
      setK(leer);
      setOffen(false);
    } catch (err) {
      beiFehler(err instanceof Error ? err.message : "Anlegen fehlgeschlagen.");
    } finally {
      setLaeuft(false);
    }
  }

  if (!offen)
    return (
      <div className="knopfzeile" style={{ marginBottom: "1.25rem" }}>
        <button className="knopf" onClick={() => setOffen(true)}>
          + Neue Firma
        </button>
      </div>
    );

  return (
    <section className="karte">
      <h2>Neue Firma</h2>
      <form onSubmit={absenden}>
        <div className="feld">
          <label htmlFor="k-firma">Firma</label>
          <input
            id="k-firma"
            required
            autoFocus
            value={k.firma}
            onChange={(e) => setzen({ firma: e.target.value })}
          />
        </div>

        <div className="raster">
          <div className="feld">
            <label htmlFor="k-nr">Kundennummer</label>
            <input
              id="k-nr"
              placeholder="wird vergeben"
              value={k.kundennr ?? ""}
              onChange={(e) => setzen({ kundennr: e.target.value })}
            />
          </div>
          <div className="feld">
            <label htmlFor="k-str">Straße</label>
            <input
              id="k-str"
              value={k.strasse ?? ""}
              onChange={(e) => setzen({ strasse: e.target.value })}
            />
          </div>
          <div className="feld">
            <label htmlFor="k-plz">PLZ</label>
            <input
              id="k-plz"
              inputMode="numeric"
              value={k.plz ?? ""}
              onChange={(e) => setzen({ plz: e.target.value })}
            />
          </div>
          <div className="feld">
            <label htmlFor="k-ort">Ort</label>
            <input
              id="k-ort"
              value={k.ort ?? ""}
              onChange={(e) => setzen({ ort: e.target.value })}
            />
          </div>
          <div className="feld">
            <label htmlFor="k-km">Entfernung (km)</label>
            <input
              id="k-km"
              inputMode="numeric"
              placeholder="einfach, ab Hasede"
              value={k.entfernung_km ?? ""}
              onChange={(e) =>
                setzen({ entfernung_km: e.target.value === "" ? null : Number(e.target.value) })
              }
            />
          </div>
          <div className="feld">
            <label htmlFor="k-ap">Ansprechpartner</label>
            <input
              id="k-ap"
              value={k.ansprechpartner ?? ""}
              onChange={(e) => setzen({ ansprechpartner: e.target.value })}
            />
          </div>
          <div className="feld">
            <label htmlFor="k-mail">E-Mail</label>
            <input
              id="k-mail"
              type="email"
              value={k.email ?? ""}
              onChange={(e) => setzen({ email: e.target.value })}
            />
          </div>
          <div className="feld">
            <label htmlFor="k-tel">Telefon</label>
            <input
              id="k-tel"
              value={k.telefon ?? ""}
              onChange={(e) => setzen({ telefon: e.target.value })}
            />
          </div>
          <div className="feld">
            <label htmlFor="k-ust">USt-IdNr.</label>
            <input
              id="k-ust"
              value={k.ust_id ?? ""}
              onChange={(e) => setzen({ ust_id: e.target.value })}
            />
          </div>
          <div className="feld">
            <label htmlFor="k-zz">Zahlungsziel (Tage)</label>
            <input
              id="k-zz"
              type="number"
              min={0}
              max={180}
              value={k.zahlungsziel_tage ?? 14}
              onChange={(e) => setzen({ zahlungsziel_tage: Number(e.target.value) || 0 })}
            />
          </div>
        </div>

        <div className="knopfzeile">
          <button className="knopf" type="submit" disabled={laeuft || !k.firma.trim()}>
            {laeuft ? "Wird angelegt …" : "Anlegen"}
          </button>
          <button
            type="button"
            className="knopf sekundaer"
            onClick={() => {
              setOffen(false);
              setK(leer);
            }}
          >
            Abbrechen
          </button>
        </div>
      </form>
    </section>
  );
}

/* ── Massenimport ────────────────────────────────────────────────────────── */

function Massenimport({ beiFertig }: { beiFertig: (m: string) => void }) {
  const [gelesen, setGelesen] = useState<DateiErgebnis | null>(null);
  const [dateiname, setDateiname] = useState("");
  const [lesefehler, setLesefehler] = useState<string | null>(null);
  const [fortschritt, setFortschritt] = useState<number | null>(null);
  const [ergebnis, setErgebnis] = useState<ImportErgebnis | null>(null);
  const eingabe = useRef<HTMLInputElement>(null);

  function zuruecksetzen() {
    setGelesen(null);
    setDateiname("");
    setLesefehler(null);
    setErgebnis(null);
    setFortschritt(null);
    if (eingabe.current) eingabe.current.value = "";
  }

  async function dateiGewaehlt(e: React.ChangeEvent<HTMLInputElement>) {
    const datei = e.target.files?.[0];
    if (!datei) return;
    setLesefehler(null);
    setErgebnis(null);
    setDateiname(datei.name);
    try {
      setGelesen(await leseKundendatei(datei));
    } catch (err) {
      setGelesen(null);
      setLesefehler(nachladeFehler(err));
    }
  }

  async function importieren() {
    if (!gelesen) return;
    const alle = gelesen.kunden;
    const gesamt: ImportErgebnis = { uebernommen: [], abgewiesen: [] };

    setFortschritt(0);
    try {
      // Blockweise senden, damit auch eine Liste mit tausend Zeilen durchgeht.
      for (let i = 0; i < alle.length; i += BLOCK) {
        const teil = await api.post<ImportErgebnis>("/kunden/import", {
          kunden: alle.slice(i, i + BLOCK),
          zeilenversatz: i + 1, // +1 wegen der Überschriftenzeile
        });
        gesamt.uebernommen.push(...teil.uebernommen);
        gesamt.abgewiesen.push(...teil.abgewiesen);
        setFortschritt(Math.min(i + BLOCK, alle.length));
      }
      setErgebnis(gesamt);
      setGelesen(null);
      beiFertig(
        `${gesamt.uebernommen.length} von ${alle.length} Zeilen übernommen` +
          (gesamt.abgewiesen.length ? `, ${gesamt.abgewiesen.length} übersprungen.` : "."),
      );
    } catch (err) {
      setLesefehler(err instanceof Error ? err.message : "Import fehlgeschlagen.");
    } finally {
      setFortschritt(null);
    }
  }

  function ladeVorlage() {
    // BOM voranstellen, sonst zeigt Excel die Umlaute falsch an.
    const url = URL.createObjectURL(
      new Blob(["﻿" + vorlageCsv()], { type: "text/csv;charset=utf-8" }),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = "kunden-vorlage.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <section className="karte">
      <h2>Kundenliste hochladen</h2>
      <p className="hinweistext" style={{ marginTop: 0, marginBottom: "0.9rem" }}>
        Excel (.xlsx, .xls) oder CSV. Die erste Zeile muss die Spaltenüberschriften enthalten;
        die Zuordnung erkennt gängige Bezeichnungen selbst. Pflicht ist nur der Firmenname.
      </p>

      <div className="knopfzeile" style={{ marginBottom: "0.9rem" }}>
        <input
          ref={eingabe}
          type="file"
          accept=".xlsx,.xls,.csv,.txt"
          onChange={dateiGewaehlt}
          style={{ maxWidth: 340 }}
        />
        <button className="knopf schlicht" onClick={ladeVorlage}>
          Vorlage herunterladen
        </button>
      </div>

      {lesefehler && (
        <div className="meldung fehler" role="alert">
          {lesefehler}
        </div>
      )}

      {gelesen && (
        <>
          <div className="meldung hinweis">
            <strong>{gelesen.kunden.length}</strong> Zeilen aus „{dateiname}“ gelesen.
            {gelesen.ignoriert.length > 0 && (
              <>
                {" "}
                Nicht zugeordnet: {gelesen.ignoriert.join(", ")}.
              </>
            )}
          </div>

          <div className="zuordnung">
            {gelesen.zuordnung.map((z) => (
              <span key={z.spalte} className="zuordnung-paar">
                <span className="zuordnung-quelle">{z.spalte}</span>
                <span aria-hidden="true">→</span>
                <span>{z.feld}</span>
              </span>
            ))}
          </div>

          <div className="tabelle-scroll" style={{ maxHeight: 300, overflowY: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th>Zeile</th>
                  <th>Firma</th>
                  <th>PLZ / Ort</th>
                  <th>Ansprechpartner</th>
                </tr>
              </thead>
              <tbody>
                {gelesen.kunden.slice(0, 20).map((k, i) => (
                  <tr key={i}>
                    <td className="zahl">{i + 2}</td>
                    <td>{k.firma}</td>
                    <td>{[k.plz, k.ort].filter(Boolean).join(" ") || "—"}</td>
                    <td>{k.ansprechpartner ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {gelesen.kunden.length > 20 && (
              <p className="hinweistext">… und {gelesen.kunden.length - 20} weitere.</p>
            )}
          </div>

          <div className="knopfzeile" style={{ marginTop: "1rem" }}>
            <button className="knopf" onClick={importieren} disabled={fortschritt !== null}>
              {fortschritt !== null
                ? `Wird importiert … ${fortschritt}/${gelesen.kunden.length}`
                : `${gelesen.kunden.length} Kunden importieren`}
            </button>
            <button className="knopf sekundaer" onClick={zuruecksetzen} disabled={fortschritt !== null}>
              Abbrechen
            </button>
          </div>
        </>
      )}

      {ergebnis && (
        <>
          <div className="meldung erfolg">
            {ergebnis.uebernommen.length} Kunden übernommen.
          </div>
          {ergebnis.abgewiesen.length > 0 && (
            <>
              <h3 style={{ fontSize: "0.95rem" }}>
                {ergebnis.abgewiesen.length} Zeilen übersprungen
              </h3>
              <div className="tabelle-scroll" style={{ maxHeight: 240, overflowY: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      <th>Zeile</th>
                      <th>Firma</th>
                      <th>Grund</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ergebnis.abgewiesen.map((a, i) => (
                      <tr key={i}>
                        <td className="zahl">{a.zeile}</td>
                        <td>{a.firma || "—"}</td>
                        <td style={{ color: "var(--engelke-rot)" }}>{a.grund}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <div className="knopfzeile" style={{ marginTop: "0.9rem" }}>
            <button className="knopf sekundaer" onClick={zuruecksetzen}>
              Weitere Datei
            </button>
          </div>
        </>
      )}
    </section>
  );
}

/* ── Sortiment ───────────────────────────────────────────────────────────── */

/**
 * Die üblichen Mehlsorten eines Kunden.
 *
 * Sie belegen in der Preisfindung die Artikelauswahl vor — wer für die
 * Bäckerei Musterbrot einen Preis nennt, will nicht erst neun Roggenmehle
 * wegklicken.
 *
 * Leer heisst ausdrücklich „nicht gepflegt" und damit alle Artikel. Es gibt
 * keinen Zustand „bezieht nichts"; den bräuchte niemand, und er wäre von
 * „noch nicht gepflegt" nicht zu unterscheiden.
 */
function Sortiment({
  kunde,
  artikel,
  beiFehler,
  beiGespeichert,
}: {
  kunde: Kunde;
  artikel: { id: string; sku: string; bezeichnung: string }[];
  beiFehler: (m: string) => void;
  beiGespeichert: (ids: string[]) => void;
}) {
  const [gewaehlt, setGewaehlt] = useState<string[]>(kunde.produkt_ids ?? []);
  const [laeuft, setLaeuft] = useState(false);

  async function sichern() {
    setLaeuft(true);
    try {
      const a = await api.put<{ produkt_ids: string[] }>(`/kunden/${kunde.id}/sortiment`, {
        produkt_ids: gewaehlt,
      });
      beiGespeichert(a.produkt_ids);
    } catch (e) {
      beiFehler(e instanceof Error ? e.message : "Sortiment konnte nicht gespeichert werden.");
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <div style={{ padding: "0.4rem 0 0.8rem" }}>
      <p className="hinweistext" style={{ marginTop: 0 }}>
        Übliche Mehlsorten von <strong>{kunde.firma}</strong>. Sie erscheinen in der Preisfindung
        vorausgewählt. Nichts angekreuzt heißt <strong>alle Artikel</strong> — nicht keine.
      </p>

      <div className="knopfzeile" style={{ marginTop: 0 }}>
        <button type="button" className="knopf schlicht" onClick={() => setGewaehlt([])}>
          leeren
        </button>
        <span className="hinweistext">
          {gewaehlt.length ? `${gewaehlt.length} von ${artikel.length}` : "alle Artikel"}
        </span>
      </div>

      <div className="kaestchenraster">
        {artikel.map((a) => (
          <label key={a.id} className="kaestchen">
            <input
              type="checkbox"
              checked={gewaehlt.includes(a.id)}
              onChange={() =>
                setGewaehlt((g) =>
                  g.includes(a.id) ? g.filter((x) => x !== a.id) : [...g, a.id],
                )
              }
            />
            <span>
              {a.bezeichnung}
              <span className="hinweistext"> {a.sku}</span>
            </span>
          </label>
        ))}
      </div>

      <div className="knopfzeile">
        <button className="knopf" onClick={() => void sichern()} disabled={laeuft}>
          {laeuft ? "Wird gespeichert …" : "Sortiment speichern"}
        </button>
      </div>
    </div>
  );
}
